import '@xyflow/react/dist/style.css'
import { Box, useToken } from '@chakra-ui/react'
import type { ReactFlowProps } from '@xyflow/react'
import { ReactFlow, Background, ConnectionMode, type ColorMode } from '@xyflow/react'
import { memo, useCallback, useMemo, useState } from 'react'
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom'

import { defaultEdgeOptions } from './components/edges/helpers'
import nodeTypes from './components/nodes/nodeTypes'

import { useColorMode, useColorModeValue } from '@app/components/ui/color-mode'
import useGetEdges from '@app/hooks/useGetEdges'
import useGetIsSelecting from '@app/hooks/useGetIsSelecting'
import useGetNodes from '@app/hooks/useGetNodes'
import useGetObject from '@app/hooks/useGetObject'
import useIsMobile from '@app/hooks/useIsMobile'
import BackgroundChangeLoader from '@app/pages/strategies/components/backgroundChangeLoader'
import Edge from '@app/pages/strategies/components/edges/edge'
import HelperLinesRenderer from '@app/pages/strategies/components/helperLines'
import MapFiltering from '@app/pages/strategies/components/map/mapFiltering'
import MapMenu from '@app/pages/strategies/components/map/mapMenu'
import MultiSelectToolbar from '@app/pages/strategies/components/multiSelectToolbar'
import { useStrategyMapContext } from '@app/pages/strategies/strategyMapContext'
import useInteraction from '@app/pages/strategies/useInteraction'
import useMapEvents from '@app/pages/strategies/useMapEvents'
import { getHelperLines } from '@app/pages/strategies/utils'
import { useStore } from '@app/store'
import type { DomainEdge, DomainNode } from '@app/types'

const Map = () => {
  const { nodeId, strategyId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { canEdit } = useStrategyMapContext()
  const strategy = useGetObject(strategyId, 'strategy')
  const { colorMode } = useColorMode()
  const { onNodeDragStop, onSelectionDragStop, onEdgeConnect, onEdgeDelete, onDragOver, onDrop, getRootProps } =
    useInteraction(strategyId)
  const onBeforeDelete = useStore.use.onBeforeDelete()
  const onNodesChange = useStore.use.onNodesChange()
  const onEdgesChange = useStore.use.onEdgesChange()
  const onNodesDelete = useStore.use.onNodesDelete()
  const highlightPath = useStore.use.highlightPath()
  const setIsSelecting = useStore.use.setIsSelecting()
  const [selectedIds, setSelectedIds] = useState<string>('')
  useMapEvents(strategyId)

  const [helperLineHorizontal, setHelperLineHorizontal] = useState<number | undefined>(undefined)
  const [helperLineVertical, setHelperLineVertical] = useState<number | undefined>(undefined)

  const nodes = useGetNodes(strategyId)
  const edges = useGetEdges(strategyId)

  const edgeTypes = useMemo(() => ({ custom: Edge }), [])

  const [lightDotColor, darkDotColor, lightHelperColor, darkHelperColor] = useToken('colors', [
    'gray.400',
    'gray.500',
    'gray.500',
    'gray.400'
  ])
  const dotColor = useColorModeValue(lightDotColor, darkDotColor)
  const helperColor = useColorModeValue(lightHelperColor, darkHelperColor)

  const isSelecting = useGetIsSelecting()
  const boxProps = getRootProps()

  const onEdgeDoubleClick = useCallback(
    (_, edge) => {
      navigate(`/strategy/${strategyId}/map/edges/${edge.id}`)
    },
    [navigate, strategyId]
  )

  const onBeforeDelete1 = useCallback(
    (nodesAndEdges) => onBeforeDelete(nodesAndEdges, strategyId),
    [onBeforeDelete, strategyId]
  )
  const onNodesChange1 = useCallback(
    (changes) => {
      setHelperLineHorizontal(undefined)
      setHelperLineVertical(undefined)

      // this will be true if it's a single node being dragged
      // inside we calculate the helper lines and snap position for the position where the node is being moved to
      if (changes.length === 1 && changes[0].type === 'position' && changes[0].dragging && changes[0].position) {
        const helperLines = getHelperLines(changes[0], nodes)

        // if we have a helper line, we snap the node to the helper line position
        // this is being done by manipulating the node position inside the change object
        changes[0].position.x = helperLines.snapPosition.x ?? changes[0].position.x
        changes[0].position.y = helperLines.snapPosition.y ?? changes[0].position.y

        // if helper lines are returned, we set them so that they can be displayed
        setHelperLineHorizontal(helperLines.horizontal)
        setHelperLineVertical(helperLines.vertical)
      }

      return onNodesChange(changes)
    },
    [nodes, onNodesChange]
  )
  const onNodesDelete1 = useCallback((nds) => onNodesDelete(strategyId, nds), [onNodesDelete, strategyId])
  const onSelectionChange: ReactFlowProps<DomainNode, DomainEdge>['onSelectionChange'] = useCallback(
    ({ nodes: selectedNodes }) => {
      const ids = JSON.stringify(selectedNodes.map((node) => node.id).sort())
      // If ids are the same as before, do nothing.
      // onSelectionChange fires even if the selection hasn't changed, e.g. when a collapsed node is clicked
      if (ids === selectedIds) {
        return
      }

      setSelectedIds(ids)
      if (selectedNodes.length && nodeId) {
        const selectedNode = selectedNodes[0]
        const { id, type } = selectedNode
        const domainObjectId = id.replace(/\D/g, '')
        const skip = ['section', 'note', 'commentThread', 'strategy'].includes(type)

        // Don't navigate if we're already on the correct node
        if (!location.pathname.includes(`${type}/${domainObjectId}`) && !skip) {
          navigate(`${type}/${domainObjectId}`)
        }
      }

      highlightPath(selectedNodes, strategyId)
    },
    [highlightPath, location.pathname, navigate, nodeId, selectedIds, strategyId]
  )
  const onSelectionStart = useCallback(() => setIsSelecting(true), [setIsSelecting])
  const onSelectionEnd = useCallback(() => setIsSelecting(false), [setIsSelecting])

  const isMobile = useIsMobile()

  // do not crash if the user deletes the currently-viewed strategy
  if (!strategy) {
    return null
  }

  const { backgroundChangeInProgress = true } = strategy || {}

  return (
    <>
      {backgroundChangeInProgress && <BackgroundChangeLoader />}
      <Box {...boxProps} h="100%">
        <ReactFlow<DomainNode, DomainEdge>
          nodes={nodes}
          edges={edges}
          connectionMode={ConnectionMode.Loose}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          onBeforeDelete={onBeforeDelete1}
          onNodesChange={onNodesChange1}
          onEdgesChange={onEdgesChange}
          onConnect={onEdgeConnect}
          onEdgeDoubleClick={canEdit && onEdgeDoubleClick}
          onEdgesDelete={onEdgeDelete}
          onNodesDelete={onNodesDelete1}
          onNodeDragStop={onNodeDragStop}
          onSelectionDragStop={onSelectionDragStop} // covers multi-select and move
          onDragOver={onDragOver}
          onDrop={onDrop}
          onSelectionStart={onSelectionStart}
          onSelectionEnd={onSelectionEnd}
          onSelectionChange={onSelectionChange}
          nodeDragThreshold={1}
          nodesDraggable={canEdit}
          selectNodesOnDrag={false}
          snapToGrid
          fitView
          minZoom={0.15}
          maxZoom={4}
          proOptions={{ account: 'paid-pro', hideAttribution: true }}
          fitViewOptions={{ padding: 0.2 }}
          deleteKeyCode={canEdit ? ['Backspace', 'Delete'] : null}
          connectionRadius={120}
          isValidConnection={(edge) => edge.source !== edge.target}
          elevateNodesOnSelect={false}
          colorMode={colorMode as ColorMode}
        >
          <Background gap={20} size={2} color={dotColor} />
          {!isMobile && canEdit && <MapFiltering strategyId={strategyId} />}
          {!isMobile && canEdit && <MultiSelectToolbar strategyId={strategyId} isSelecting={isSelecting} />}
          {!isMobile && <MapMenu strategyId={strategyId} />}
          <HelperLinesRenderer
            horizontal={helperLineHorizontal}
            vertical={helperLineVertical}
            helperColor={helperColor}
          />
        </ReactFlow>
        <Outlet />
      </Box>
    </>
  )
}

Map.displayName = 'Map'

export default memo(Map)
